import { ServicePriceBearer } from '@lib/api-interface';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, Panel, RenderIf, Snackbar, TableFilterItem, ToggleButtons, UsecaseButton, YearMonthFilterBar, YearMonthFilterBarValues } from '@lib/ui-components';
import { Add } from '@mui/icons-material';
import { Checkbox, InputLabel, TextField } from '@mui/material';
import { priceTypeEnumValues } from '../projects/view-project/clients-tab/headquarters-branches/project-branch-view/branch-services/shared-components/shared';
import { useUserState } from '../../../state/UserState';
import { AuthoritiesEnum } from '../../../domain/enums/authorities.enum';
import { GreenRedDotIndicator } from '../../../libs/ui-components/components/indicator/green-red-dot-indicator';
import axios from 'axios';
import { ServerPaginatedTable } from '../../../libs/ui-components/components/data-table/server-paginated-table';

export interface InvoicesProps {
  bearer: ServicePriceBearer;
  onDefaultFiltersUpdated?: (filters: Array<TableFilterItem>) => void;
  instanceFiltersRef: React.MutableRefObject<TableFilterItem[]>;
}

export const PaginatedInvoices = ({ bearer, onDefaultFiltersUpdated, instanceFiltersRef }: InvoicesProps) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [activePage, setActivePage] = useState(0);
  const [branches, setBranches] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState(new Array<any>());
  const [batchInvoiceNumber, setBatchInvoiceNumber] = useState('');
  const [batchInvoiceComment, setBatchInvoiceComment] = useState('');

  const isFilterUpdating = useRef(false);
  const [tableFilters, setTableFilters] = useState<TableFilterItem[]>([]);

  const { t } = useTranslation();
  const owners = ['CLIENT', 'SUBCONTRACTOR'];
  const userState = useUserState();
  const canAddInvoiceNumber = userState.authorities.includes(AuthoritiesEnum.ORGANIZATION_ADMIN) || userState.authorities.includes(AuthoritiesEnum.PROJECT_INVOICE);
  const [showBatchUpdateCalledMessage, setShowBatchUpdateCalledMessage] = useState(false);
  const [hasPendingUpdateCommand, setHasPendingUpdateCommand] = useState(null);

  const now = new Date();
  const month = now.getMonth() + 1;
  const yearMonthNow = `${now.getFullYear()}-${month > 9 ? month : '0' + month}`;
  const savedFilters = sessionStorage.getItem('invoicesFilters');
  const initialFilters = savedFilters
    ? JSON.parse(savedFilters)
    : {
      date: yearMonthNow,
      filter: '',
    };
  const [activeFilters, setActiveFilters] = React.useState<YearMonthFilterBarValues>(initialFilters);

  useEffect(() => {
    if (instanceFiltersRef.current.length === 0 && savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        if (onDefaultFiltersUpdated && parsedFilters.tableFilters) {
          onDefaultFiltersUpdated(parsedFilters.tableFilters);
          setTableFilters(parsedFilters.tableFilters);
        }
      } catch (e) {
        console.error('Error parsing saved filters', e);
      }
    }
  }, []);

  function handleAggregationChange(value: boolean) {
    setBranches(value);
    setRefreshKey((prevKey) => prevKey + 1);
  }

  function handlePageChange(index: number) {
    setActivePage(index);
    setRefreshKey((prevKey) => prevKey + 1);
  }

  async function handleBatchChangeSuccess() {
    setBatchInvoiceNumber('');
    setBatchInvoiceComment('');
    setRefreshKey((prevKey) => prevKey + 1);
    setShowBatchUpdateCalledMessage(true);
  }

  function saveActiveFilters(filters: YearMonthFilterBarValues, newTableFilters?: TableFilterItem[]) {
    const combinedFilters = {
      ...filters,
      tableFilters: newTableFilters || tableFilters,
    };
    sessionStorage.setItem('invoicesFilters', JSON.stringify(combinedFilters));
  }

  function handleTableFiltersChanged(filters: TableFilterItem[]) {
    if (isFilterUpdating.current) return;

    isFilterUpdating.current = true;
    setTableFilters(filters);
    saveActiveFilters(activeFilters, filters);
    if (onDefaultFiltersUpdated) {
      onDefaultFiltersUpdated(filters);
    }

    setTimeout(() => {
      isFilterUpdating.current = false;
    }, 50);
  }

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('/api/invoice/hasPendingUpdateNumberAndCommentCommand');
        setHasPendingUpdateCommand(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [refreshKey]);

  const batchUpdateFields = React.useMemo(() => {
    if (selectedInvoices.length === 0) return null;

    const commonFields = (
      <>
        <TextField
          inputProps={{ 'data-test-id': 'batch-invoice-comment-text-field' }}
          key='batch-invoice-comment-text-field'
          variant='standard'
          label={t('comment')}
          onChange={(event) => setBatchInvoiceComment(event.target.value)}
        />
        <UsecaseButton
          dataTestId='add-invoice-number-and-comment-button'
          groupName='invoice'
          useCaseName={canAddInvoiceNumber ? 'add-invoice-number-and-comment' : 'add-invoice-comment'}
          buttonLabel={t('assign')}
          variant='outlined'
          hiddenValues={{
            invoiceIds: selectedInvoices.flatMap((invoice) => invoice.invoiceIds),
            invoiceNumber: canAddInvoiceNumber ? batchInvoiceNumber : undefined,
            comment: batchInvoiceComment,
          }}
          isDisabled={hasPendingUpdateCommand !== null && hasPendingUpdateCommand}
          onSaved={handleBatchChangeSuccess}
        />
        <GreenRedDotIndicator showRedLight={hasPendingUpdateCommand !== null && hasPendingUpdateCommand} />
      </>
    );

    return (
      <HStack align='left' gap='1em'>
        {canAddInvoiceNumber && (
          <TextField
            inputProps={{ 'data-test-id': 'batch-invoice-number-text-field' }}
            key='batch-invoice-number-text-field'
            variant='standard'
            label={t('invoiceNumber')}
            onChange={(event) => setBatchInvoiceNumber(event.target.value)}
          />
        )}
        {commonFields}
      </HStack>
    );
  }, [selectedInvoices, batchInvoiceNumber, batchInvoiceComment, canAddInvoiceNumber, hasPendingUpdateCommand]);

  const fetchUrl = `/api/invoice/db-view${branches ? '' : '-aggregated'}`;

  const dataTable = React.useMemo(() => {
    return (
      <ServerPaginatedTable
        selectable={true}
        selectableOptions={{
          onSelectedRowsChange: setSelectedInvoices,
          comparatorField: 'invoiceIds',
        }}
        refreshKey={refreshKey}
        fetchUrl={fetchUrl}
        fetchFilters={{
          priceBearer: bearer,
          invoiceOwner: owners[activePage],
          yearMonth: activeFilters.date,
        }}
        responsePath={{
          content: `_embedded.invoice${branches ? '' : 'Aggregated'}DbViews`,
          totalElements: 'page.totalElements',
        }}
        defaultTableFilters={tableFilters.length > 0 ? tableFilters : instanceFiltersRef.current}
        onDefaultTableFiltersUpdated={handleTableFiltersChanged}
        excel={true}
        defaultPageSize={25}
        rowsPerPageOptions={[25, 50, 100, 250, 500, 1000]}
        initialSort={{ columnIndex: 1, ascending: true }}
        columns={[
          { key: 'yearMonth', label: t('month'), type: 'date-year-month', excludeFromFilter: true },
          { key: 'companyName', label: t('companyName'), type: 'text' },
          { key: 'companyCreditor', label: t('creditor'), type: 'text' },
          { key: 'companyDebitor', label: t('debitor'), type: 'text' },
          { key: 'branchName', label: t('branchName'), type: 'text' },
          { key: 'projectName', label: t('projectName'), type: 'text' },
          { key: 'projectNumber', label: t('projectNumber'), type: 'text' },
          { key: 'branchAddress.town', label: t('branchCity'), type: 'text' },
          { key: 'branchAddress.street', label: t('branchStreet'), type: 'text' },
          { key: 'branchAddress.houseNum', label: t('houseNum'), type: 'text' },
          { key: 'branchAddress.postalCode', label: t('postcode'), type: 'text' },
          { key: 'resource', label: t('wasteCode'), type: 'text' },
          { key: 'resourceDescription', label: t('wasteDescription'), type: 'text' },
          { key: 'variableAnnotations', label: t('variableAnnotation'), type: 'string-array-multiline', excludeFromFilter: true },
          {
            key: 'invoiceBearer',
            label: t('concerns'),
            type: 'enum',
            joinedColumns: [{ key: 'implementationDate', label: '', type: 'date' }],
            enumValues: [
              { value: 'CLIENT_TRANSPORT', label: t('clientTransport') },
              { value: 'SUBCONTRACTOR_TRANSPORT', label: t('subcontractorTransport') },
              { value: 'CLIENT_COLLECTION', label: t('clientCollection') },
              { value: 'SUBCONTRACTOR_COLLECTION', label: t('subcontractorCollection') },
              { value: 'CLIENT_LOADING_TIME', label: t('clientLoadingTime') },
              { value: 'SUBCONTRACTOR_LOADING_TIME', label: t('subcontractorLoadingTime') },
              { value: 'CLIENT_FEES', label: t('clientFees') },
              { value: 'SUBCONTRACTOR_FEES', label: t('subcontractorFees') },
              { value: 'CLIENT_FIRE_PROTECTION', label: t('clientFireProtection') },
              { value: 'SUBCONTRACTOR_FIRE_PROTECTION', label: t('subcontractorFireProtection') },
              { value: 'CLIENT', label: t('clientLease') },
              { value: 'SUBCONTRACTOR', label: t('subcontractorLease') },
            ],
          },
          { key: 'servicePrice.stringValue', label: t('pricePerUnit'), type: 'text' },
          { key: 'totalReportedValue', label: t('declaredSum'), type: 'numeric', numericPrecision: 4 },
          {
            key: 'totalPricePLN',
            label: `${t('totalPrice')} (PLN)`,
            type: 'numeric',
            numericPrecision: 2,
            bold: true,
          },
          {
            key: 'totalPriceEUR',
            label: `${t('totalPrice')} (EUR)`,
            type: 'numeric',
            numericPrecision: 2,
            bold: true,
          },
          { key: 'priceType', label: t('priceType'), type: 'enum', enumValues: priceTypeEnumValues(t) },
          {
            key: 'einvoiceAgreed',
            label: t('acceptElectronicInvoices'),
            type: 'boolean',
          },
          {
            key: 'organizationAsSide',
            label: t('organizationAsSideShortened'),
            type: 'boolean',
          },
          { key: branches ? 'invoiceNumber' : 'invoiceNumbers', label: t('invoiceNumber'), type: 'text' },
          { key: branches ? 'comment' : 'comments', label: t('comment'), type: 'text' },
        ]}
        rowOptions={[
          {
            isVisible: () => canAddInvoiceNumber,
            renderer: (row: any) => (
              <UsecaseButton
                dataTestId={`add-invoice-number-${row.invoiceId}`}
                onSaved={async () => setRefreshKey((prevKey) => prevKey + 1)}
                groupName='invoice'
                useCaseName='add-invoice-number'
                startIcon={<Add />}
                buttonLabel={t('invoiceNumber')}
                fields={[
                  {
                    field: 'invoiceNumber',
                    label: t('invoiceNumber'),
                    type: 'text',
                    defaultValue: branches ? row.invoiceNumber : row.invoiceNumbers,
                  },
                ]}
                hiddenValues={{ invoiceIds: row.invoiceIds }}
              />
            ),
          },
          {
            renderer: (row: any) => (
              <UsecaseButton
                dataTestId={`add-invoice-comment-${row.invoiceId}`}
                onSaved={async () => setRefreshKey((prevKey) => prevKey + 1)}
                groupName='invoice'
                useCaseName='add-invoice-comment'
                startIcon={<Add />}
                buttonLabel={t('comment')}
                fields={[
                  {
                    field: 'comment',
                    label: t('comment'),
                    type: 'text',
                    defaultValue: branches ? row.comment : row.comments,
                  },
                ]}
                hiddenValues={{ invoiceIds: row.invoiceIds }}
              />
            ),
          },
        ]}
      />
    );
  }, [refreshKey, bearer, activeFilters.date, branches, activePage, tableFilters]);

  return (
    <>
      <Panel>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ width: '33%' }}>
            <YearMonthFilterBar
              onFilterChanged={(filters) => {
                setActiveFilters(filters);
                saveActiveFilters(filters);
                setRefreshKey((prevKey) => prevKey + 1);
              }}
              initialMonthYear={activeFilters.date}
            />
          </div>
          <div style={{ width: '33%', display: 'flex', justifyContent: 'center' }}>
            <ToggleButtons labels={[t('client'), t('subcontractor')]} activeIndex={activePage} onChange={(index) => handlePageChange(index)} />
          </div>
          <div style={{ width: '33%', display: 'flex', justifyContent: 'right' }}>
            <HStack>
              <InputLabel>{t('branches')}</InputLabel>
              <Checkbox checked={branches} onChange={(event, checked) => handleAggregationChange(checked)} />
            </HStack>
          </div>
        </div>
      </Panel>
      <Panel>{batchUpdateFields}</Panel>
      {dataTable}
      <RenderIf true={showBatchUpdateCalledMessage}>
        <Snackbar message={t('addInvoiceNumberAndCommentProcessStarted')} severity={'info'} onClose={() => setShowBatchUpdateCalledMessage(false)} />
      </RenderIf>
    </>
  );
};
